import React from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'react-relay';
import get from 'lodash.get';
import { getQueryParam } from '../../../utils/uriUtils';
import { SharedModifyPortfolioItems } from '../../../mutations/SharedModifyPortfolioItems';
import { SharedParallelizedQueryRenderer } from '../../../shared/SharedParallelizedQueryRenderer';

export class FavoritesProvider extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            variables: {
                userId: '',
                fetchUser: false,
                userIds: [],
                selectedItemIds: [],
                loadPortfolioData: false,
                shouldFillInFolder: false,
            },
        };
    }

    componentDidMount() {
        if (this.shouldPerformAutoFavorite()) {
            this.handleAutoFavorite();
        } else if (this.props.userId) {
            this.setVariables();
        }
    }

    componentDidUpdate(prevProps) {
        if (this.shouldPerformAutoFavorite(prevProps)) {
            this.handleAutoFavorite();
        } else if (
            (!prevProps.userId && this.props.userId) ||
            (this.props.userId && prevProps.itemIds.toString() !== this.props.itemIds.toString())
        ) {
            this.setVariables();
        }
    }

    shouldPerformAutoFavorite(prevProps = {}) {
        return (
            this.props.shouldAutoFavorite &&
            getQueryParam(location.href, 'follow') === 'item' &&
            (!prevProps.userId && this.props.userId)
        );
    }

    handleAutoFavorite() {
        const { itemIds, environment, userId } = this.props;

        SharedModifyPortfolioItems.commit(environment, {
            action: 'ADD',
            itemId: itemIds[0],
            portfolioType: 'HEART',
            userId,
            onCompleted: () => this.setVariables(),
        });
    }

    setVariables() {
        const { userId, itemIds } = this.props;

        this.setState({
            variables: {
                userId: userId,
                fetchUser: !!userId,
                userIds: [userId],
                selectedItemIds: itemIds,
                loadPortfolioData: true,
                shouldFillInFolder: true,
            },
        });
    }

    render() {
        const { environment, renderVariables = {} } = this.props;

        return (
            <SharedParallelizedQueryRenderer
                query={graphql`
                    query FavoritesProviderQuery(
                        $loadPortfolioData: Boolean = false
                        $selectedItemIds: [String] = []
                        $userIds: [String] = []
                        $userId: String = ""
                        $fetchUser: Boolean = false
                        $shouldFillInFolder: Boolean = false
                    ) {
                        viewer {
                            isEnabledDesignerConnection: isEnabled(
                                key: "postContactSellerDesignerConnection"
                            )
                            user(userId: $userId) @include(if: $fetchUser) {
                                isVerifiedTrade
                            }
                            ...FolderWrapper_viewer
                                @arguments(
                                    selectedItemIds: $selectedItemIds
                                    shouldFillInFolder: $shouldFillInFolder
                                    userIds: $userIds
                                )
                            ...HeartWrapper_viewer
                                @arguments(
                                    loadPortfolioData: $loadPortfolioData
                                    selectedItemIds: $selectedItemIds
                                    userIds: $userIds
                                )
                        }
                    }
                `}
                environment={environment}
                variables={this.state.variables}
                renderVariables={renderVariables}
                render={({ data }) => {
                    const { viewer = null } = data || {};
                    const { userId, itemIds, disable } = this.props;
                    const userIds = userId ? [userId] : [];

                    const childrenProps = {
                        userId,
                        itemIds,
                        userIds,
                        disable,
                        location: this.props.location,
                        environment,
                        fetchHeart: this.props.fetchHeart,
                        fetchFolder: this.props.fetchFolder,
                        singlePortfolioItem: true,
                        loadPortfolioItemData: true,
                        selectedItemIds: itemIds,
                        loadPortfolioData: !!userId,
                        shouldFillInFolder: !!userId,
                        viewer,
                        isEnabledDesignerConnection:
                            get(viewer, 'isEnabledDesignerConnection') || false,
                        isVerifiedTrade: get(viewer, 'user.isVerifiedTrade') || false,
                    };

                    return this.props.children({ props: childrenProps });
                }}
            />
        );
    }
}

FavoritesProvider.defaultProps = {
    fetchHeart: true,
    fetchFolder: true,
    shouldAutoFavorite: false,
};

FavoritesProvider.propTypes = {
    children: PropTypes.func,
    disable: PropTypes.bool,
    environment: PropTypes.object.isRequired,
    fetchFolder: PropTypes.bool.isRequired,
    fetchHeart: PropTypes.bool.isRequired,
    itemIds: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
    location: PropTypes.string,
    renderVariables: PropTypes.object,
    shouldAutoFavorite: PropTypes.bool,
    userId: PropTypes.string,
};
